import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import {
  ProposalBoxType,
  ProposalContext,
  ProposalEvent,
  ProposalMove,
  ProposalOutcome,
  ProposalStage,
  ProposalStatus,
} from "work/proposals/__constants/proposal-lifecycle";
import WorkCriteriaSchema, {
  decodeWorkCriteria,
  encodeWorkCriteria,
  optionalEach,
} from "work/work-records/_schemas/work-criteria-schema";
import { z } from "zod";

export type ProposalFilterJSON = z.infer<typeof ProposalFilterSchema.ProposalFilterJSONZ>;
export type ProposalFilterParametersProps = z.infer<typeof ProposalFilterSchema.ProposalFilterZ>;

const ID_KEYS = [
  "vendorTeamMemberId",
  "supersededByProposalId",
  "clientReviewerId",
  "vendorReviewerId",
  "eventActorUserId",
] as const;
const INSTANT_KEYS = [
  "minCreated",
  "maxCreated",
  "minResponseDueBy",
  "maxResponseDueBy",
  "minUpdated",
  "maxUpdated",
  "minEventDate",
  "maxEventDate",
  "minDeleted",
  "maxDeleted",
] as const;
const YES_NO_KEYS = [
  "hasStart",
  "isNegotiable",
  "hasResponseDueBy",
  "isSuperseded",
  "isArchived",
  "isActive",
  "isDeleted",
  "groupByRequest",
] as const;

const KEYS = {
  ids: [...WorkCriteriaSchema.KEYS.ids, ...ID_KEYS],
  instants: [...WorkCriteriaSchema.KEYS.instants, ...INSTANT_KEYS],
};

/**
 * A chip field the API takes one or several values of (#6709): several chips in one group widen the
 * list (#6712), and the API reads a repeated parameter as one `IN`.
 */
function oneOrMany<T extends z.ZodType>(value: T) {
  return z.union([value, z.array(value)]).optional();
}

/**
 * The criteria of a proposal list: the ones every work record shares, and the ones only a proposal
 * has. `Work.Proposals.ProposalFilterParameters` on the server.
 */
export default class ProposalFilterSchema {
  public static readonly ProposalFilterJSONZ = z.object({
    ...WorkCriteriaSchema.WorkCriteriaJSONShape,
    ...optionalEach(ID_KEYS, z.guid()),
    ...optionalEach(INSTANT_KEYS, z.iso.datetime({ offset: true })),
    ...optionalEach(YES_NO_KEYS, z.boolean()),
    status: z.enum(ProposalStatus).optional(),
    event: z.enum(ProposalEvent).optional(),
    boxType: z.enum(ProposalBoxType).optional(),
    stage: oneOrMany(z.enum(ProposalStage)),
    move: oneOrMany(z.enum(ProposalMove)),
    outcome: z.enum(ProposalOutcome).optional(),
    context: z.enum(ProposalContext).optional(),
  });
  public static readonly ProposalFilterZ = z.object({
    ...WorkCriteriaSchema.WorkCriteriaShape,
    ...optionalEach(ID_KEYS, z.instanceof(Guid)),
    ...optionalEach(INSTANT_KEYS, DateSchema.type),
    ...optionalEach(YES_NO_KEYS, z.boolean()),
    status: z.enum(ProposalStatus).optional(),
    event: z.enum(ProposalEvent).optional(),
    boxType: z.enum(ProposalBoxType).optional(),
    stage: oneOrMany(z.enum(ProposalStage)),
    move: oneOrMany(z.enum(ProposalMove)),
    outcome: z.enum(ProposalOutcome).optional(),
    context: z.enum(ProposalContext).optional(),
  });

  public static validateJSON(values: unknown): ProposalFilterParametersProps {
    return parseOrThrow(ProposalFilterSchema.ProposalFilterZ, values, {
      logError: true,
      schemaName: "ProposalFilterSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is ProposalFilterParametersProps {
    try {
      ProposalFilterSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: ProposalFilterJSON): ProposalFilterParametersProps {
    const parsed = parseOrThrow(ProposalFilterSchema.ProposalFilterJSONZ, data, {
      logError: true,
      schemaName: "ProposalFilterSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return decodeWorkCriteria<ProposalFilterParametersProps>(parsed, KEYS);
  }

  public static serialize(props: ProposalFilterParametersProps): ProposalFilterJSON {
    const parsed = ProposalFilterSchema.validateJSON(props);
    return encodeWorkCriteria<ProposalFilterJSON>(parsed);
  }
}
